import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckinsService } from '@/shared/api/rest/services/checkins.service'
import { ChildByQrCode } from '@/pages/user/teacher/check-in-out/types'

interface CheckInInput {
    qrCode: string
    checkInBy?: string
    branch: string
    session?: string
}

interface CheckOutInput {
    qrCode: string
    checkOutBy?: string
    branch: string
    checkinCode: string
    session?: string
}

/**
 * Look up a child by QR code. Mirrors the Apollo `useQuery(CHILD_BY_QRCODE)`
 * surface: `{ loading, data: { getChildByQRCode } }`. Parent/contact records
 * are name-enriched by the backend.
 */
export function useChildByQrCode(
    qrCode: string,
    options?: { onError?: (error: Error) => void }
) {
    const { isLoading, data, error } = useQuery({
        queryKey: ['child-by-qr', qrCode],
        enabled: !!qrCode,
        retry: false,
        gcTime: 0,
        staleTime: 0,
        queryFn: async () => {
            try {
                const child = await CheckinsService.childByQrCode(qrCode)
                return { getChildByQRCode: child } as ChildByQrCode
            } catch (err) {
                const e = err instanceof Error ? err : new Error(String(err))
                options?.onError?.(e)
                throw e
            }
        }
    })

    return { loading: isLoading, data, error: error ?? undefined }
}

/**
 * Check a child in. Mirrors Apollo's `useMutation` tuple:
 * `[checkIn, { loading }]`, where `checkIn({ variables: { input } })` resolves
 * `{ checkin: <record> }` and `onCompleted` receives the same.
 */
export function useCheckinChild(options?: {
    onCompleted?: (data: { checkin: any }) => void
}) {
    const queryClient = useQueryClient()
    const { mutateAsync, isPending } = useMutation({
        mutationFn: (args: { variables: { input: CheckInInput } }) =>
            CheckinsService.checkin(args.variables.input),
        onSuccess: (record) => {
            options?.onCompleted?.({ checkin: record })
            queryClient.invalidateQueries({ queryKey: ['checkout-queue'] })
        }
    })

    const checkIn = async (args: { variables: { input: CheckInInput } }) => {
        const record = await mutateAsync(args)
        return { data: { checkin: record } }
    }

    return [checkIn, { loading: isPending }] as const
}

/**
 * Check a child out. Mirrors Apollo's `[checkOut, { loading }]`.
 */
export function useCheckoutChild() {
    const queryClient = useQueryClient()
    const { mutateAsync, isPending } = useMutation({
        mutationFn: (args: { variables: { input: CheckOutInput } }) =>
            CheckinsService.checkout(args.variables.input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['checkout-queue'] })
        }
    })

    const checkOut = async (args: { variables: { input: CheckOutInput } }) => {
        const record = await mutateAsync(args)
        return { data: { checkout: record } }
    }

    return [checkOut, { loading: isPending }] as const
}
